<script setup lang="ts">
import { api } from '@/api';
import { EntityWithCommentType, type NumericQuantityDto } from '@/api/generator';
import Comments from '@/components/comments/Comments.vue';
import { useAmountComments } from '@/components/comments/useAmountComments';
import { getDefaultDeleteConfirmationOptions } from '@/components/confirmationModals/defaults';
import type { EntityInfo } from '@/components/confirmationModals/types';
import EntityChangeBlock from '@/components/EntityHistory/EntityChangeBlock.vue';
import VeeAttachments from '@/components/forms/Attachments/VeeAttachments.vue';
import VeeAvailability from '@/components/forms/Availability/VeeAvailability.vue';
import FormSection from '@/components/forms/FormSection/FormSection.vue';
import VeeTextarea from '@/components/forms/Textarea/VeeTextarea.vue';
import VeeTextField from '@/components/forms/TextField/VeeTextField.vue';
import { useFormMode } from '@/components/forms/useFormMode';
import { useFormTab } from '@/components/forms/useFormTab';
import HistoryEvents from '@/components/history/HistoryEvents.vue';
import BasePageNavigation from '@/components/pages/BasePage/BasePageNavigation.vue';
import { defaultPageTabs, getDefaultPageActions } from '@/components/pages/BasePage/defaults';
import type { PageAction } from '@/components/pages/BasePage/types';
import { type TabDefinition } from '@/components/Tabs/types';
import { routenames } from '@/router/routenames';
import { isPredefinedEntity, translateEntityProperty } from '@/utils/helper.ts';
import { QuantityMetaInfo } from '@/views/quantities/types.ts';
import { useTranslation } from 'i18next-vue';
import { useFormValues } from 'vee-validate';
import { computed, provide, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useDeleteNumericQuantityWithConfirmation } from '../utils';
import UseInEvaluationsForm from './useInEvaluations/UseInEvaluationsForm.vue';
import UseInSetupForm from './UseInSetupForm.vue';

const router = useRouter();

const { t } = useTranslation();

type Quantity = NumericQuantityDto;

const values = useFormValues<Quantity>();
const mode = useFormMode();

const { action: deleteAction } = useDeleteNumericQuantityWithConfirmation();

const { commentsTabTitle, commentsChanged } = useAmountComments(values);

const { getIndicatorState } = useFormTab();

const tabs = computed(() => {
  return {
    administrative: {
      ...defaultPageTabs.administrative,
      title: t('AdministrativeInformation'),
      indicatorState: getIndicatorState(['name', 'description', 'attachments']),
    },

    useInEvaluations: {
      title: t('UseInEvaluation_other'),
      hideContentSheet: true,
      indicatorState: getIndicatorState(['evaluationUsage']),
    },

    useInSetups: {
      title: t('UseInSetup_other'),
      hideContentSheet: true,
      indicatorState: getIndicatorState(['setupUsage', 'setupGeometries']),
    },

    comments: {
      ...defaultPageTabs.comments,
      hidden: mode.value === 'add',
      title: commentsTabTitle.value,
    },

    history: {
      ...defaultPageTabs.history,
      hidden: defaultPageTabs.history.hidden || true,
      unmountTab: true,
    },
  } satisfies TabDefinition;
});

const actions = computed<PageAction[]>(() => {
  const defaultPageActions = getDefaultPageActions(mode.value, values.value);

  return [
    {
      ...defaultPageActions.edit,
      to: {
        name: routenames.quantities.edit,
        params: { id: values.value?.id },
      },
    },
    {
      ...defaultPageActions.delete,
      callback: async () => {
        if (values.value.id) {
          const { result } = await deleteAction({
            param: values.value.id,
            confirmationOptions: getDefaultDeleteConfirmationOptions(
              {
                ...(values.value as EntityInfo),
                typeName: 'Quantity_one',
              },
              t,
            ),
          });

          if (result?.success) {
            router.push({
              name: routenames.quantities.overview,
            });
          }
        }
      },
    },
  ];
});

watch(values.value, () => {
  const setupUsage = values.value?.setupUsage;
  const setupGeometries = setupUsage?.setupGeometries;

  if (setupUsage && Array.isArray(setupGeometries)) {
    setupUsage.useSetupGeometry = setupGeometries.length > 0;
  }
});

watch(values.value, () => {
  const evaluationUsage = values.value?.evaluationUsage;
  const evaluationGeometries = evaluationUsage?.evaluationGeometries;

  if (evaluationUsage && Array.isArray(evaluationGeometries)) {
    evaluationUsage.useEvaluationGeometry = evaluationGeometries.length > 0;
  }
});

const isInEditMode = mode.value === 'edit';
const isInAddMode = mode.value === 'add';
const isReferenced = computed(() => values.value.isReferenced ?? false);
const isPredefined = computed(() => isPredefinedEntity(values.value));
const isEvaluationUsageEnabled = computed(
  () => values.value.evaluationUsage?.isUsageEnabled ?? false,
);
const isSetupUsageEnabled = computed(() => values.value.setupUsage?.isUsageEnabled ?? false);

provide<QuantityMetaInfo>('QuantityMetaInfo', {
  isInEditMode,
  isInAddMode,
  isReferenced: isReferenced.value,
  isPredefined: isPredefined.value,
  isSetupUsageEnabled: isSetupUsageEnabled,
  isEvaluationUsageEnabled: isEvaluationUsageEnabled,
});
</script>

<template>
  <BasePageNavigation :tabs="tabs" :actions="actions">
    <!-- ADMINISTRATIVE -->
    <template #administrative>
      <FormSection>
        <v-row class="justify-sm-start">
          <v-col cols="3">
            <VeeTextField
              :label="$t('Name')"
              name="name"
              :mode="isPredefined ? 'details' : undefined"
              :outputConverter="() => translateEntityProperty('name', values)"
              :placeholder="$t('Required')"
            />
          </v-col>

          <v-col cols="7">
            <VeeTextarea
              :label="$t('Description')"
              name="description"
              :mode="isPredefined ? 'details' : undefined"
              :outputConverter="() => translateEntityProperty('description', values)"
            />
            <VeeAttachments class="mt-1" name="attachments" data-testid="Description_Attachments" />
          </v-col>

          <v-col cols="2">
            <VeeAvailability name="isAvailable" :disabled="mode !== 'details'" />
            <EntityChangeBlock v-if="mode === 'details'" :entity="values" />
          </v-col>
        </v-row>
      </FormSection>
    </template>

    <template #useInEvaluations>
      <UseInEvaluationsForm name="evaluationUsage" />
    </template>

    <template #useInSetups>
      <UseInSetupForm name="setupUsage" />
    </template>

    <template #comments>
      <FormSection>
        <Comments
          v-if="values.id"
          :id="values.id"
          :entityWithCommentType="EntityWithCommentType.Quantity"
          @change="commentsChanged"
        />
      </FormSection>
    </template>

    <template #history>
      <HistoryEvents
        v-if="values.id"
        :id="values.id"
        :historyApiFn="api.numericQuantitiesApi.getQuantityHistory.bind(api.numericQuantitiesApi)"
      />
    </template>
  </BasePageNavigation>
</template>
